import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowRight, BarChart3, ShoppingBag, Building2, Zap } from 'lucide-react';
interface SizzleForBlockProps {
  badge: string;
  badgeIcon: React.ReactNode;
  badgeColor: string;
  title: string;
  description: string;
  activationHighlight: string;
  href: string;
  linkLabel: string;
  accentColor: string;
}
function SizzleForBlock(t0) {
  const $ = _c(22);
  const {
    badge,
    badgeIcon,
    badgeColor,
    title,
    description,
    activationHighlight,
    href,
    linkLabel,
    accentColor
  } = t0;
  const t1 = `mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ${badgeColor}`;
  let t2;
  if ($[0] !== badge || $[1] !== badgeIcon || $[2] !== t1) {
    t2 = <div className={t1}>{badgeIcon}{badge}</div>;
    $[0] = badge;
    $[1] = badgeIcon;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = <h3 className="mb-3 text-2xl font-bold text-foreground md:text-3xl">{title}</h3>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== description) {
    t4 = <p className="mb-5 text-base leading-relaxed text-muted-foreground">{description}</p>;
    $[6] = description;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Zap className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== activationHighlight) {
    t6 = <div className="mb-6 flex items-start gap-3 rounded-xl border border-border bg-muted/50 p-4">{t5}<p className="text-sm leading-relaxed text-foreground">{activationHighlight}</p></div>;
    $[9] = activationHighlight;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = `inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium hover:underline ${accentColor}`;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArrowRight className="h-3.5 w-3.5" />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== href || $[13] !== linkLabel || $[14] !== t7) {
    t9 = <Link href={href} className={t7}>{linkLabel}{t8}</Link>;
    $[12] = href;
    $[13] = linkLabel;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t4 || $[19] !== t6 || $[20] !== t9) {
    t10 = <div className="group relative rounded-2xl border bg-card p-8 shadow-sm transition-all duration-300 hover:shadow-lg">{t2}{t3}{t4}{t6}{t9}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
export function SizzleForSectionV3() {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-14 text-center"><p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-primary-600">For the people and businesses helping discovery, trial, and enjoyment happen</p><h2 className="mb-4 text-4xl font-black text-gray-900">Help great products get discovered, tried, chosen, and enjoyed again</h2><p className="mx-auto max-w-3xl text-lg text-muted-foreground">Whether you make products, source and sell them, or create places and moments where people discover them, Sizzle helps you turn first interest into real experiences and repeat momentum.</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SizzleForBlock badge="For Brands" badgeIcon={<BarChart3 className="h-4 w-4" />} badgeColor="bg-purple-100 text-purple-600" title="Launch ideas that earn attention and repeat" description="Run sampling campaigns, promotions, limited-time offers, retail demos, pop-ups, and other initiatives that help more people discover your products and bring them into their routines." activationHighlight="Use Sizzle to turn discovery into trial, trial into feedback, and feedback into smarter next moves." href="/for-brands" linkLabel="Learn more about Sizzle for Brands" accentColor="text-purple-600" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SizzleForBlock badge="For Buyers & Retailers" badgeIcon={<ShoppingBag className="h-4 w-4" />} badgeColor="bg-blue-100 text-blue-600" title="Bring fresh products and better reasons to visit" description="Browse brands, explore new products, and collaborate on demos, promotions, and experiences that help people discover something worth trying in your store." activationHighlight="Sizzle helps connect product discovery with real-world retail moments that create traffic, trial, and repeat purchase." href="/for-independent-retailers" linkLabel="Learn more about Sizzle for Retailers" accentColor="text-blue-600" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <section id="sizzle-for-section" className="bg-muted/30 py-20"><div className="container mx-auto px-4 sm:px-6 lg:px-8">{t0}<div className="grid grid-cols-1 gap-8 lg:grid-cols-3">{t1}{t2}<SizzleForBlock badge="For Hosts" badgeIcon={<Building2 className="h-4 w-4" />} badgeColor="bg-cyan-100 text-cyan-600" title="Create fun experiences for your people" description="Offer your workplace, campus, venue, or event space as a place for product discovery. Help people try something new while giving brands a meaningful way to show up." activationHighlight="Sizzle helps match your audience and environment with activations that feel relevant, enjoyable, and easy to host." href="/host-opportunities" linkLabel="Learn more about Sizzle for Hosts" accentColor="text-cyan-600" /></div></div></section>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}